import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import {
  onIdTokenChanged,
  signInAnonymously,
  signOut as fbSignOut,
  GoogleAuthProvider,
  OAuthProvider,
  EmailAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signInWithCredential,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  linkWithPopup,
  linkWithRedirect,
  linkWithCredential,
  type AuthProvider as FbAuthProvider,
  type User as FirebaseUser,
} from 'firebase/auth'
import { httpsCallable } from 'firebase/functions'
import { auth, db, functions } from '@/firebase'
import { createUser, getUserById, HandleTakenError } from '@/services/userService'
import { claimOwnership, type ClaimOwnershipParams } from '@/services/tagService'
import { track } from '@/services/analytics'
import {
  AUTH_ERROR_KEY,
  clearPending,
  hasPending,
  isMobileBrowser,
  readPending,
  stashPending,
} from '@/services/authRedirect'

/**
 * useAuth — the single source of auth truth, shared via context.
 *
 * Anonymous-first: visitors get an anonymous session on demand (the post flow
 * calls ensureAnonymousSession). "Upgrading" links a Google/Apple/email
 * credential onto the same anonymous uid so the user keeps their post history.
 *
 * OAuth uses a popup on desktop but a full-page REDIRECT on mobile browsers
 * (popups are unreliable there — see authRedirect.ts). Because a redirect
 * reloads the page, the completion logic (create profile · migrate posts ·
 * claim tag) is shared between the inline desktop path and `finishRedirect`,
 * which runs at app start via <OAuthRedirectGate>.
 */

type Provider = 'google' | 'apple'

/** The result of an OAuth/email account attempt, or a sentinel when the browser
 *  is navigating away to complete the flow via redirect. */
export type AccountResult = 'redirecting' | { existing: boolean; handle: string }

/** What the redirect handler resolves to on return from the provider. */
export type RedirectOutcome =
  | { status: 'created'; next: string }
  | { status: 'existing'; next: string }
  | { status: 'signedin'; next: string }
  | { status: 'error'; message: string }

export interface AuthContextValue {
  user: FirebaseUser | null
  loading: boolean
  isAnonymous: boolean
  isAccount: boolean
  isAdmin: boolean
  ensureAnonymousSession: () => Promise<FirebaseUser>
  /** Create an account (or adopt an existing one) with a Google/Apple credential.
   *  Resolves to `'redirecting'` on mobile — the caller should stop and let the
   *  page navigate away; the flow finishes via <OAuthRedirectGate> on return. */
  startAccountOAuth: (
    provider: Provider,
    opts: { handle: string; next: string; claim?: ClaimOwnershipParams },
  ) => Promise<AccountResult>
  /** Create an account (or adopt an existing one) with email + password. Never
   *  redirects. */
  createAccountEmail: (
    email: string,
    password: string,
    opts: { handle: string; claim?: ClaimOwnershipParams },
  ) => Promise<Exclude<AccountResult, 'redirecting'>>
  /** Sign in to a returning account via Google/Apple. `'redirecting'` on mobile. */
  startSignInOAuth: (provider: Provider, next: string) => Promise<'redirecting' | void>
  /** Sign in to a returning email/password account. */
  signInWithEmail: (email: string, password: string) => Promise<void>
  /** Send a password-reset email. Resolves on success AND when no account
   *  exists for `email` (`auth/user-not-found` is swallowed here, at the
   *  service boundary) — a "no such account" response would otherwise let a
   *  caller enumerate registered emails. Other failures (e.g.
   *  `auth/too-many-requests`) propagate for the caller to surface. */
  sendPasswordReset: (email: string) => Promise<void>
  signOut: () => Promise<void>
  /** Finish a redirect-based OAuth flow on app load. Null when none is pending. */
  finishRedirect: () => Promise<RedirectOutcome | null>
  /** True when a redirect flow is mid-flight (loader gate reads this on paint). */
  hasPendingRedirect: () => boolean
}

/** Firebase auth error codes meaning "this credential already has an account". */
const ACCOUNT_EXISTS_CODES = new Set([
  'auth/email-already-in-use',
  'auth/credential-already-in-use',
  'auth/account-exists-with-different-credential',
  'auth/provider-already-linked',
])
const isAccountExists = (err: unknown): boolean =>
  typeof err === 'object' &&
  err !== null &&
  'code' in err &&
  ACCOUNT_EXISTS_CODES.has((err as { code: string }).code)

/** Apple is exposed via Firebase's generic OAuth provider. */
const appleProvider = (): OAuthProvider => {
  const provider = new OAuthProvider('apple.com')
  provider.addScope('email')
  provider.addScope('name')
  return provider
}
const providerFor = (p: Provider): FbAuthProvider =>
  p === 'google' ? new GoogleAuthProvider() : appleProvider()

/** GA4 `method` param — Firebase's own provider id convention. */
type AuthMethod = 'password' | 'google.com' | 'apple.com'
const methodFor = (p: Provider): AuthMethod => (p === 'google' ? 'google.com' : 'apple.com')

/** The OAuth credential carried on a link/sign-in error (Google or Apple), so an
 *  "account already exists" failure can sign in without a second prompt. */
const credFromError = (err: unknown) =>
  GoogleAuthProvider.credentialFromError(err as never) ??
  OAuthProvider.credentialFromError(err as never)

const AuthContext = createContext<AuthContextValue | null>(null)

/** Fire the post-upgrade backfill/migration. Best-effort; logged on failure. */
const runAccountUpgrade = async (previousUid: string): Promise<void> => {
  try {
    const callable = httpsCallable<{ previousUid: string }, void>(functions, 'onAccountUpgrade')
    await callable({ previousUid })
  } catch (error) {
    // Non-fatal: the account exists; a retry path can reconcile later.
    console.error('onAccountUpgrade failed', error)
  }
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<FirebaseUser | null>(null)
  const [loading, setLoading] = useState(true)
  const [isAdmin, setIsAdmin] = useState(false)
  // Mirrored as primitive state because linking a credential MUTATES the same
  // FirebaseUser object — setUser(next) with an unchanged reference doesn't
  // re-render, so a derived `user.isAnonymous` stays stale and a brand-new
  // owner keeps seeing "posting as raver_####". Initialized false to match the
  // old derived value pre-resolution (`!!null?.isAnonymous`); initializing true
  // flips every anonymous-gated branch during loading and breaks the gifted-tag
  // flow.
  const [isAnonymous, setIsAnonymous] = useState(false)

  useEffect(() => {
    // onIdTokenChanged, not onAuthStateChanged: the latter does NOT fire when an
    // anonymous session is upgraded by linking a credential (same uid, same
    // object), which is exactly when isAnonymous flips.
    const unsub = onIdTokenChanged(auth, async (next) => {
      setUser(next)
      setIsAnonymous(!!next?.isAnonymous)
      // Admin is a custom claim baked into the ID token; read it server-issued.
      if (next) {
        try {
          const token = await next.getIdTokenResult()
          setIsAdmin(token.claims.admin === true)
        } catch {
          setIsAdmin(false)
        }
      } else {
        setIsAdmin(false)
      }
      setLoading(false)
    })
    return unsub
  }, [])

  const ensureAnonymousSession = useCallback(async (): Promise<FirebaseUser> => {
    // Wait for Firebase to restore the persisted session first. On a fresh load
    // (reload or deep-link straight into /t/{id}/post) currentUser is still null
    // for a moment, and signing in anonymously here would REPLACE a signed-in
    // user with a fresh raver_####.
    await auth.authStateReady()
    if (auth.currentUser) return auth.currentUser
    const cred = await signInAnonymously(auth)
    return cred.user
  }, [])

  /**
   * The shared "the session now has a real credential" tail, run by every
   * successful path (desktop popup + mobile redirect return):
   *  - new account: reserve the handle, then backfill the handle onto the
   *    user's own posts (linking preserves the uid).
   *  - existing account (uid changed): re-home the anonymous session's posts.
   *  - onboarding: claim the tag once the account exists.
   *
   * This is also the single centralized point that reports `sign_up`/`login`
   * for every account-linking path (desktop popup and mobile redirect-return
   * alike), so callers never fire these themselves.
   */
  const completeAccount = useCallback(
    async (opts: {
      mode: 'new' | 'existing'
      method: AuthMethod
      handle?: string
      claim?: ClaimOwnershipParams
      anonUid: string | null
    }): Promise<void> => {
      const uid = auth.currentUser?.uid
      if (!uid) throw new Error('Sign-in did not complete')
      if (opts.mode === 'new') {
        if (!opts.handle) throw new Error('Missing handle')
        await createUser(db, { userId: uid, handle: opts.handle })
        await runAccountUpgrade(uid)
        track('sign_up', { method: opts.method })
      } else {
        if (opts.anonUid && opts.anonUid !== uid) {
          await runAccountUpgrade(opts.anonUid)
        }
        track('login', { method: opts.method })
      }
      if (opts.claim) {
        await claimOwnership(functions, opts.claim)
        // New-owner claims land here (claim rides the account creation);
        // already-signed-in owners claim via OnboardingPage.finish() instead —
        // the two paths are disjoint, so tag_claim fires exactly once.
        track('tag_claim', { tag_id: opts.claim.tagId })
      }
    },
    [],
  )

  const startAccountOAuth = useCallback(
    async (
      provider: Provider,
      opts: { handle: string; next: string; claim?: ClaimOwnershipParams },
    ): Promise<AccountResult> => {
      const current = await ensureAnonymousSession()
      const anonUid = current.isAnonymous ? current.uid : null

      if (isMobileBrowser()) {
        stashPending({
          intent: 'create',
          provider,
          next: opts.next,
          anonUid,
          handle: opts.handle,
          claim: opts.claim,
        })
        await linkWithRedirect(current, providerFor(provider))
        return 'redirecting'
      }

      // Desktop: link via popup. If the credential already has an account, sign
      // into it with the credential carried on the error (no second prompt).
      try {
        await linkWithPopup(current, providerFor(provider))
      } catch (err) {
        if (!isAccountExists(err)) throw err
        const cred = credFromError(err)
        if (!cred) throw err
        await signInWithCredential(auth, cred)
        await completeAccount({ mode: 'existing', method: methodFor(provider), claim: opts.claim, anonUid })
        const existing = await getUserById(db, auth.currentUser!.uid)
        return { existing: true, handle: existing?.handle ?? opts.handle }
      }
      await completeAccount({ mode: 'new', method: methodFor(provider), handle: opts.handle, claim: opts.claim, anonUid })
      return { existing: false, handle: opts.handle }
    },
    [ensureAnonymousSession, completeAccount],
  )

  const createAccountEmail = useCallback(
    async (
      email: string,
      password: string,
      opts: { handle: string; claim?: ClaimOwnershipParams },
    ): Promise<Exclude<AccountResult, 'redirecting'>> => {
      const current = await ensureAnonymousSession()
      const anonUid = current.isAnonymous ? current.uid : null
      try {
        await linkWithCredential(current, EmailAuthProvider.credential(email, password))
      } catch (err) {
        if (!isAccountExists(err)) throw err
        // Email errors carry no credential — sign in with what they typed.
        await signInWithEmailAndPassword(auth, email, password)
        await completeAccount({ mode: 'existing', method: 'password', claim: opts.claim, anonUid })
        const existing = await getUserById(db, auth.currentUser!.uid)
        return { existing: true, handle: existing?.handle ?? opts.handle }
      }
      await completeAccount({ mode: 'new', method: 'password', handle: opts.handle, claim: opts.claim, anonUid })
      return { existing: false, handle: opts.handle }
    },
    [ensureAnonymousSession, completeAccount],
  )

  const startSignInOAuth = useCallback(
    async (provider: Provider, next: string): Promise<'redirecting' | void> => {
      const prev = auth.currentUser
      const anonUid = prev?.isAnonymous ? prev.uid : null
      if (isMobileBrowser()) {
        stashPending({ intent: 'signin', provider, next, anonUid })
        await signInWithRedirect(auth, providerFor(provider))
        return 'redirecting'
      }
      await signInWithPopup(auth, providerFor(provider))
      if (anonUid && auth.currentUser && auth.currentUser.uid !== anonUid) {
        await runAccountUpgrade(anonUid)
      }
      track('login', { method: methodFor(provider) })
    },
    [],
  )

  const signInWithEmail = useCallback(
    async (email: string, password: string): Promise<void> => {
      const prev = auth.currentUser
      const anonUid = prev?.isAnonymous ? prev.uid : null
      await signInWithEmailAndPassword(auth, email, password)
      if (anonUid && auth.currentUser && auth.currentUser.uid !== anonUid) {
        await runAccountUpgrade(anonUid)
      }
      track('login', { method: 'password' })
    },
    [],
  )

  const sendPasswordReset = useCallback(async (email: string): Promise<void> => {
    try {
      await sendPasswordResetEmail(auth, email)
    } catch (err) {
      // Swallow "no such account" into success — otherwise this endpoint is an
      // account-enumeration oracle. Anything else (e.g. too-many-requests)
      // propagates for the caller to surface.
      if (
        typeof err === 'object' &&
        err !== null &&
        'code' in err &&
        (err as { code: string }).code === 'auth/user-not-found'
      ) {
        return
      }
      throw err
    }
  }, [])

  const finishRedirect = useCallback(async (): Promise<RedirectOutcome | null> => {
    const pending = readPending()
    if (!pending) return null
    clearPending()
    try {
      const result = await getRedirectResult(auth)
      // No result + no throw = the user backed out of the provider screen.
      if (!result) return null

      if (pending.intent === 'signin') {
        if (pending.anonUid && auth.currentUser && auth.currentUser.uid !== pending.anonUid) {
          await runAccountUpgrade(pending.anonUid)
        }
        track('login', { method: methodFor(pending.provider) })
        return { status: 'signedin', next: pending.next }
      }
      await completeAccount({
        mode: 'new',
        method: methodFor(pending.provider),
        handle: pending.handle,
        claim: pending.claim,
        anonUid: pending.anonUid,
      })
      return { status: 'created', next: pending.next }
    } catch (err) {
      // create + "already exists": the link failed because this Google/Apple
      // identity already owns an account — sign into it with the error's credential.
      if (pending.intent === 'create' && isAccountExists(err)) {
        const cred = credFromError(err)
        if (cred) {
          try {
            await signInWithCredential(auth, cred)
            await completeAccount({
              mode: 'existing',
              method: methodFor(pending.provider),
              claim: pending.claim,
              anonUid: pending.anonUid,
            })
            return { status: 'existing', next: pending.next }
          } catch {
            /* fall through to the generic message */
          }
        }
        return { status: 'error', message: 'You already have an account with that — try signing in.' }
      }
      if (err instanceof HandleTakenError) {
        return { status: 'error', message: 'That handle was just taken — try another.' }
      }
      return { status: 'error', message: err instanceof Error ? err.message : 'Sign-in didn’t finish.' }
    }
  }, [completeAccount])

  const signOut = useCallback(async (): Promise<void> => {
    await fbSignOut(auth)
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      isAnonymous,
      isAccount: !!user && !isAnonymous,
      isAdmin,
      ensureAnonymousSession,
      startAccountOAuth,
      createAccountEmail,
      startSignInOAuth,
      signInWithEmail,
      sendPasswordReset,
      signOut,
      finishRedirect,
      hasPendingRedirect: hasPending,
    }),
    [
      user,
      loading,
      isAnonymous,
      isAdmin,
      ensureAnonymousSession,
      startAccountOAuth,
      createAccountEmail,
      startSignInOAuth,
      signInWithEmail,
      sendPasswordReset,
      signOut,
      finishRedirect,
    ],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

/** Consume the auth context. Throws if used outside <AuthProvider>. */
export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>')
  return ctx
}

export { AUTH_ERROR_KEY }
